import { QueryClient, useMutation, UseMutationOptions } from '@tanstack/react-query';
import { IGqlOperation } from './GqlOperation';

export class GqlMutation<
  Vars extends Record<string, unknown>,
  Data extends Record<string, unknown>,
> {
  private _onSettled: null | ((args: { variables: Vars }) => void);
  private _operation: IGqlOperation<Vars, Data>;
  private _queryClient: QueryClient;

  constructor(args: {
    onSettled?: (args: { variables: Vars }) => void;
    operation: IGqlOperation<Vars, Data>;
    queryClient: QueryClient;
  }) {
    this._onSettled = args.onSettled ?? null;
    this._operation = args.operation;
    this._queryClient = args.queryClient;
  }

  get document() {
    return this._operation.document;
  }

  get operationName() {
    return this._operation.operationName;
  }

  async request(
    variables: Vars,
    options?: {
      signal?: AbortSignal;
    },
  ) {
    try {
      return await this._operation.request(variables, options);
    } finally {
      this._onSettled?.({ variables });
    }
  }

  useMutation(options?: Omit<UseMutationOptions<Data, Error, Vars>, 'mutationFn'>) {
    const mutationFn = (variables: Vars) => this._operation.request(variables);
    // we know useMutation must be called within a react environment
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const mutation = useMutation(
      {
        mutationFn,
        ...options,
      },
      this._queryClient,
    );
    return mutation;
  }
}
